/**
 * ONE Mail phases 3–4 in the page: attachment chips (open, download, save to FusionDocs), the meeting-invite card,
 * the More menu (move to folder, flag with a due date, categories, mark unread), filters, the Sent folder, shared
 * mailboxes, out-of-office, quick replies and the Mail Pro switch.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarCheck, CalendarX, Check, Download, ExternalLink, Flag, FolderInput, HelpCircle, Inbox, MailOpen, MoreHorizontal, Paperclip, Plane, Plus, Reply, Tag, Trash2, UploadCloud, X, Zap } from "lucide-react";
import { useState } from "react";
import { api, del, post, put } from "../../lib/api";
import { RichEditor } from "../RichEditor";
import { ErrorNote } from "../ui";
import { MailComposer } from "./MailComposer";

const kb = (n: number) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
const enc = encodeURIComponent;

// ── attachments in the conversation ──
export function AttachmentChip({ messageId, gid, a }: { messageId: string; gid: string; a: { id?: string; name: string; size: number } }) {
  const save = useMutation({ mutationFn: () => post<{ url: string | null }>(`/api/mail/messages/${messageId}/files/${enc(gid)}/${enc(a.id!)}/fusiondocs`, {}) });
  if (!a.id) return <span className="chip"><Paperclip size={11} /> {a.name} <span className="muted">{kb(a.size)}</span></span>;
  const href = `/api/mail/messages/${messageId}/files/${enc(gid)}/${enc(a.id)}`;
  return (
    <span className="chip gap-1.5">
      <a className="inline-flex items-center gap-1 hover:underline" href={href} target="_blank" rel="noopener" title="Open"><Paperclip size={11} /> {a.name}</a>
      <span className="muted">{kb(a.size)}</span>
      <a href={`${href}?download=1`} title="Download" aria-label={`Download ${a.name}`}><Download size={11} /></a>
      <button type="button" title={save.isSuccess ? "Saved in FusionDocs" : "Save to FusionDocs (your area)"} aria-label={`Save ${a.name} to FusionDocs`} disabled={save.isPending || save.isSuccess} onClick={() => save.mutate()}>
        {save.isSuccess ? <Check size={11} className="text-emerald-600" /> : <UploadCloud size={11} />}
      </button>
      {save.error && <span className="text-red-600" title={(save.error as Error).message}>!</span>}
    </span>
  );
}

// ── meeting invites ──
interface Meeting { type: string; subject: string; start: string | null; end: string | null; timeZone: string; location: string | null; response: string; organizer: string; online: boolean }
export function MeetingCard({ messageId, onSuggest }: { messageId: string; onSuggest: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["mail", "meeting", messageId], queryFn: () => api<{ meeting: Meeting | null; canAnswer: boolean }>(`/api/mail/messages/${messageId}/meeting`), staleTime: 60_000 });
  const [comment, setComment] = useState("");
  const answer = useMutation({ mutationFn: (a: string) => post(`/api/mail/messages/${messageId}/meeting/${a}`, { comment: comment || undefined }), onSuccess: () => void qc.invalidateQueries({ queryKey: ["mail"] }) });
  const m = q.data?.meeting;
  if (!m || m.type !== "meetingRequest") return null;
  const t = (s: string | null) => (s ? new Date(`${s}${m.timeZone === "UTC" && !s.endsWith("Z") ? "Z" : ""}`) : null);
  const s0 = t(m.start), s1 = t(m.end);
  const answered = answer.isSuccess ? answer.variables : m.response !== "none" && m.response !== "notResponded" ? m.response : null;
  return (
    <div className="mb-2 rounded-lg border border-brand-200 bg-brand-50/60 p-2.5 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <CalendarCheck size={15} className="text-brand-700" />
        <span className="font-semibold">{m.subject}</span>
        <span className="muted text-xs">{s0 ? when(s0.toISOString()) : ""}{s1 ? `–${s1.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}` : ""}{m.location ? ` · ${m.location}` : m.online ? " · Teams" : ""} · from {m.organizer}</span>
      </div>
      {answered ? <p className="mt-1 text-xs text-emerald-700">You answered: {answered.replace("tentativelyAccept", "maybe").replace("tentativelyAccepted", "maybe")}.</p> : q.data?.canAnswer ? (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <input className="input !w-auto flex-1 !py-1 text-xs" placeholder="Add a note (optional)" value={comment} onChange={(e) => setComment(e.target.value)} maxLength={2000} />
          <button className="btn btn-primary !min-h-0 !py-1 text-xs" disabled={answer.isPending} onClick={() => answer.mutate("accept")}><Check size={13} /> Accept</button>
          <button className="btn !min-h-0 !py-1 text-xs" disabled={answer.isPending} onClick={() => answer.mutate("tentativelyAccept")}><HelpCircle size={13} /> Maybe</button>
          <button className="btn !min-h-0 !py-1 text-xs" disabled={answer.isPending} onClick={() => answer.mutate("decline")}><CalendarX size={13} /> Decline</button>
          <button className="btn !min-h-0 !py-1 text-xs" onClick={onSuggest}><Reply size={13} /> Suggest another time</button>
        </div>
      ) : <p className="muted mt-1 text-xs">Switch on Mail Pro in Mail settings to answer invites from ONE. <button className="text-brand-700 underline" onClick={onSuggest}>Suggest another time</button></p>}
      <ErrorNote error={answer.error} />
    </div>
  );
}

// ── More menu on an email: move, flag, categories, unread ──
export interface ProFields { id: string; flagged?: boolean; flagDue?: string | null; categories?: string[] }
export function FlagChips({ m }: { m: ProFields }) {
  if (!m.flagged && !m.categories?.length) return null;
  return (
    <span className="flex shrink-0 flex-wrap items-center gap-1">
      {m.flagged && <span className="chip !py-0 text-[10px] text-red-700"><Flag size={10} className="fill-red-500 text-red-500" />{m.flagDue ? ` due ${new Date(`${m.flagDue}T12:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : ""}</span>}
      {(m.categories ?? []).map((c) => <span key={c} className="chip !py-0 text-[10px]"><Tag size={10} /> {c}</span>)}
    </span>
  );
}
export function MoreMenu({ m }: { m: ProFields }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState<"" | "menu" | "move" | "flag" | "cat">("");
  const [due, setDue] = useState(m.flagDue ?? "");
  const [newCat, setNewCat] = useState("");
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["mail"] }); };
  const folders = useQuery({ queryKey: ["mail-folders"], queryFn: () => api<{ items: Array<{ id: string; name: string }> }>("/api/mail/folders"), enabled: open === "move", staleTime: 300_000 });
  const cats = useQuery({ queryKey: ["mail-categories"], queryFn: () => api<{ items: Array<{ name: string; color: string }>; fromOutlook: boolean }>("/api/mail/categories"), enabled: open === "cat", staleTime: 300_000 });
  const act = useMutation({ mutationFn: (b: { path: string; body: unknown }) => post(`/api/mail/messages/${m.id}/${b.path}`, b.body), onSuccess: () => { setOpen(""); refresh(); } });
  const setCats = (list: string[]) => act.mutate({ path: "categories", body: { categories: list } });
  const has = new Set(m.categories ?? []);
  return (
    <span className="relative">
      <button type="button" className="btn !min-h-0 !px-2 !py-1 text-xs" aria-label="More actions" onClick={() => setOpen(open ? "" : "menu")}><MoreHorizontal size={13} /> More</button>
      {open && (
        <div className="panel absolute right-0 z-30 mt-1 w-64 space-y-1 p-2 text-xs shadow-xl">
          {open === "menu" && (
            <>
              <button className="flex w-full items-center gap-2 rounded px-2 py-1.5 hover:bg-black/[0.04]" onClick={() => setOpen("move")}><FolderInput size={13} /> Move to folder…</button>
              <button className="flex w-full items-center gap-2 rounded px-2 py-1.5 hover:bg-black/[0.04]" onClick={() => setOpen("flag")}><Flag size={13} /> {m.flagged ? "Change or clear flag…" : "Flag with a due date…"}</button>
              <button className="flex w-full items-center gap-2 rounded px-2 py-1.5 hover:bg-black/[0.04]" onClick={() => setOpen("cat")}><Tag size={13} /> Categories…</button>
              <button className="flex w-full items-center gap-2 rounded px-2 py-1.5 hover:bg-black/[0.04]" onClick={() => act.mutate({ path: "unread", body: { unread: true } })}><MailOpen size={13} /> Mark unread in Outlook</button>
            </>
          )}
          {open === "move" && (
            <ul className="max-h-64 overflow-auto">
              {folders.isLoading && <li className="muted px-2 py-1">Loading folders…</li>}
              {(folders.data?.items ?? []).map((f) => (
                <li key={f.id}><button className="w-full truncate rounded px-2 py-1 text-left hover:bg-black/[0.04]" onClick={() => act.mutate({ path: "move", body: { folderId: f.id, folderName: f.name } })}>{f.name}</button></li>
              ))}
            </ul>
          )}
          {open === "flag" && (
            <div className="space-y-2 p-1">
              <label className="flex items-center gap-2">Due <input type="date" className="input !w-auto !py-0.5 text-xs" value={due} onChange={(e) => setDue(e.target.value)} /></label>
              <div className="flex gap-1.5">
                <button className="btn btn-primary btn-sm" onClick={() => act.mutate({ path: "flag", body: { on: true, due: due || null } })}><Flag size={12} /> Flag</button>
                {m.flagged && <button className="btn btn-sm" onClick={() => act.mutate({ path: "flag", body: { on: false } })}>Clear flag</button>}
              </div>
            </div>
          )}
          {open === "cat" && (
            <div className="space-y-1 p-1">
              {(cats.data?.items ?? []).map((c) => (
                <label key={c.name} className="flex items-center gap-2 rounded px-1 py-0.5 hover:bg-black/[0.04]">
                  <input type="checkbox" checked={has.has(c.name)} onChange={(e) => setCats(e.target.checked ? [...has, c.name] : [...has].filter((x) => x !== c.name))} /> {c.name}
                </label>
              ))}
              {cats.data && !cats.data.fromOutlook && <p className="muted">Your Outlook category list shows here with Mail Pro on.</p>}
              <form className="flex gap-1" onSubmit={(e) => { e.preventDefault(); if (newCat.trim()) setCats([...has, newCat.trim()]); }}>
                <input className="input !py-0.5 text-xs" placeholder="New category" value={newCat} onChange={(e) => setNewCat(e.target.value)} maxLength={60} />
                <button className="btn btn-sm"><Plus size={12} /></button>
              </form>
            </div>
          )}
          <ErrorNote error={act.error ?? folders.error ?? cats.error} />
        </div>
      )}
    </span>
  );
}

// ── filters ──
export interface MailFilter { from?: string; after?: string; before?: string; attachments?: boolean; flagged?: boolean }
export const filterQs = (f: MailFilter) => Object.entries(f).filter(([, v]) => v !== undefined && v !== "" && v !== false).map(([k, v]) => `&${k}=${enc(String(v))}`).join("");
export function FilterBar({ value, onChange }: { value: MailFilter; onChange: (f: MailFilter) => void }) {
  const set = (p: Partial<MailFilter>) => onChange({ ...value, ...p });
  const on = Object.values(value).some((v) => v !== undefined && v !== "" && v !== false);
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <input className="input !w-44 !py-1 text-xs" placeholder="From (name or address)" value={value.from ?? ""} onChange={(e) => set({ from: e.target.value })} aria-label="From" />
      <label className="muted flex items-center gap-1">After <input type="date" className="input !w-auto !py-0.5 text-xs" value={value.after ?? ""} onChange={(e) => set({ after: e.target.value || undefined })} /></label>
      <label className="muted flex items-center gap-1">Before <input type="date" className="input !w-auto !py-0.5 text-xs" value={value.before ?? ""} onChange={(e) => set({ before: e.target.value || undefined })} /></label>
      <label className="flex items-center gap-1"><input type="checkbox" checked={!!value.attachments} onChange={(e) => set({ attachments: e.target.checked || undefined })} /><Paperclip size={12} /> Has files</label>
      <label className="flex items-center gap-1"><input type="checkbox" checked={!!value.flagged} onChange={(e) => set({ flagged: e.target.checked || undefined })} /><Flag size={12} /> Flagged</label>
      {on && <button className="text-brand-700 underline" onClick={() => onChange({})}>Clear filters</button>}
    </div>
  );
}

// ── Sent and shared inboxes (live from Outlook) ──
interface LiveMsg { id: string; subject: string; preview: string; hasAttachments: boolean; webLink: string | null; isRead: boolean; to: string; fromName: string; fromEmail: string; at: string | null }
export function SentList({ q }: { q: string }) {
  const r = useQuery({ queryKey: ["mail", "sent", q], queryFn: () => api<{ items: LiveMsg[] }>(`/api/mail/sent${q ? `?q=${enc(q)}` : ""}`) });
  if (r.isLoading) return <p className="muted p-4 text-sm">Loading Sent from Microsoft 365…</p>;
  if (r.error) return <div className="p-3"><ErrorNote error={r.error} /></div>;
  return (
    <ul>
      {(r.data?.items ?? []).map((m) => (
        <li key={m.id} className="flex items-start gap-3 border-b border-[var(--line)] px-4 py-2.5 text-sm">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2"><span className="truncate font-medium">To {m.to || "—"}</span>{m.hasAttachments && <Paperclip size={12} className="muted shrink-0" />}<span className="muted ml-auto shrink-0 text-xs">{when(m.at)}</span></div>
            <div className="truncate">{m.subject}</div>
            <div className="muted truncate text-xs">{m.preview}</div>
          </div>
          {m.webLink && <a className="btn btn-sm shrink-0" href={m.webLink} target="_blank" rel="noopener noreferrer"><ExternalLink size={12} /> Outlook</a>}
        </li>
      ))}
      {r.data && !r.data.items.length && <li className="muted p-4 text-sm">Nothing found in Sent.</li>}
    </ul>
  );
}

export function SharedInbox({ address, canSend, q }: { address: string; canSend: boolean; q: string }) {
  const r = useQuery({ queryKey: ["mail", "shared", address, q], queryFn: () => api<{ items: LiveMsg[] }>(`/api/mail/shared/${enc(address)}/messages${q ? `?q=${enc(q)}` : ""}`), refetchInterval: 120_000 });
  const [openId, setOpenId] = useState<string | null>(null);
  if (r.isLoading) return <p className="muted p-4 text-sm">Loading {address}…</p>;
  if (r.error) return <div className="p-3"><ErrorNote error={r.error} /></div>;
  return (
    <ul>
      {(r.data?.items ?? []).map((m) => (
        <li key={m.id} className="border-b border-[var(--line)] px-4 py-2.5 text-sm">
          <button type="button" className="flex w-full items-start gap-2 text-left" onClick={() => setOpenId(openId === m.id ? null : m.id)}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2"><span className={`truncate ${m.isRead ? "" : "font-semibold"}`}>{m.fromName || m.fromEmail}</span>{m.hasAttachments && <Paperclip size={12} className="muted shrink-0" />}<span className="muted ml-auto shrink-0 text-xs">{when(m.at)}</span></div>
              <div className={`truncate ${m.isRead ? "" : "font-semibold"}`}>{m.subject}</div>
              {openId !== m.id && <div className="muted truncate text-xs">{m.preview}</div>}
            </div>
          </button>
          {openId === m.id && <SharedMessage address={address} id={m.id} canSend={canSend} />}
        </li>
      ))}
      {r.data && !r.data.items.length && <li className="muted p-4 text-sm">Nothing in this inbox.</li>}
    </ul>
  );
}

function SharedMessage({ address, id, canSend }: { address: string; id: string; canSend: boolean }) {
  const qc = useQueryClient();
  const r = useQuery({ queryKey: ["mail", "shared-msg", address, id], queryFn: () => api<LiveMsg & { html: string; toEmails: string[]; cc: string[] }>(`/api/mail/shared/${enc(address)}/messages/${enc(id)}`) });
  const [compose, setCompose] = useState<"" | "reply" | "reply_all" | "forward">("");
  const read = useMutation({ mutationFn: () => post(`/api/mail/shared/${enc(address)}/messages/${enc(id)}/read`, {}), onSuccess: () => void qc.invalidateQueries({ queryKey: ["mail", "shared", address] }) });
  const d = r.data;
  if (r.isLoading) return <p className="muted mt-2 text-xs">Loading…</p>;
  if (!d) return <ErrorNote error={r.error} />;
  const me = address.toLowerCase();
  const others = (l: string[]) => l.filter((e) => e && e !== me);
  return (
    <div className="mt-2 space-y-2">
      <div className="email-html rounded-lg bg-[var(--panel)] p-3 text-sm ring-1 ring-[var(--line)]" dangerouslySetInnerHTML={{ __html: d.html || "<p><em>(no text)</em></p>" }} />
      <div className="flex flex-wrap gap-1.5">
        <button className="btn btn-primary !min-h-0 !px-2 !py-1 text-xs" onClick={() => setCompose("reply")}><Reply size={13} /> Reply as {address}</button>
        <button className="btn !min-h-0 !px-2 !py-1 text-xs" onClick={() => setCompose("forward")}>Forward</button>
        {!d.isRead && <button className="btn !min-h-0 !px-2 !py-1 text-xs" disabled={read.isPending} onClick={() => read.mutate()}><Check size={13} /> Mark read</button>}
        {d.webLink && <a className="btn !min-h-0 !px-2 !py-1 text-xs" href={d.webLink} target="_blank" rel="noopener noreferrer"><ExternalLink size={13} /> Outlook</a>}
      </div>
      {compose && (
        <MailComposer initial={compose} canSend={canSend} onClose={() => setCompose("")}
          shared={{ address, gid: id, subject: d.subject, from: d.fromEmail, fromName: d.fromName, to: others(d.toEmails), cc: others(d.cc) }} />
      )}
    </div>
  );
}

// ── settings: Mail Pro, out-of-office, quick replies, shared mailboxes ──
export function MailProSwitch({ canPro }: { canPro: boolean }) {
  return canPro
    ? <p className="text-xs text-emerald-700"><Zap size={12} className="inline" /> Mail Pro is on: out-of-office, your Outlook categories, answering invites and shared mailboxes.</p>
    : (
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <a className="btn btn-primary btn-sm" href="/api/personal/microsoft/connect?pro=1"><Zap size={13} /> Switch on Mail Pro</a>
        <span className="muted">One Microsoft approval for out-of-office, your Outlook categories, answering invites and shared mailboxes.</span>
      </div>
    );
}

interface Ooo { available: boolean; status?: "disabled" | "alwaysEnabled" | "scheduled"; audience?: string; start?: string | null; end?: string | null; internal?: string; external?: string }
export function OooBanner() {
  const q = useQuery({ queryKey: ["mail", "ooo"], queryFn: () => api<Ooo>("/api/mail/ooo"), staleTime: 300_000 });
  if (!q.data?.available || q.data.status === "disabled") return null;
  return <div className="flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900"><Plane size={14} /> Out-of-office is {q.data.status === "scheduled" ? `scheduled ${q.data.start ? `from ${when(q.data.start)}` : ""} ${q.data.end ? `to ${when(q.data.end)}` : ""}` : "on"} — change it in Mail settings.</div>;
}
export function OutOfOffice() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["mail", "ooo"], queryFn: () => api<Ooo>("/api/mail/ooo") });
  const [d, setD] = useState<Ooo | null>(null);
  const cur = d ?? q.data ?? null;
  const save = useMutation({ mutationFn: (b: Ooo) => put("/api/mail/ooo", { status: b.status, audience: b.audience, start: b.start ?? undefined, end: b.end ?? undefined, internal: b.internal ?? "", external: b.external ?? "" }), onSuccess: () => { setD(null); void qc.invalidateQueries({ queryKey: ["mail", "ooo"] }); } });
  if (!cur) return null;
  if (!cur.available) return <p className="muted text-xs">Needs Mail Pro (above).</p>;
  const set = (p: Partial<Ooo>) => setD({ ...cur, ...p });
  return (
    <div className="space-y-2 text-xs">
      <div className="flex flex-wrap items-center gap-3">
        {(["disabled", "alwaysEnabled", "scheduled"] as const).map((s) => <label key={s} className="flex items-center gap-1"><input type="radio" checked={cur.status === s} onChange={() => set({ status: s })} /> {s === "disabled" ? "Off" : s === "alwaysEnabled" ? "On" : "Only between…"}</label>)}
      </div>
      {cur.status === "scheduled" && (
        <div className="flex flex-wrap gap-2">
          <input type="datetime-local" className="input !w-auto !py-0.5 text-xs" value={(cur.start ?? "").slice(0, 16)} onChange={(e) => set({ start: e.target.value })} aria-label="Starts" />
          <input type="datetime-local" className="input !w-auto !py-0.5 text-xs" value={(cur.end ?? "").slice(0, 16)} onChange={(e) => set({ end: e.target.value })} aria-label="Ends" />
        </div>
      )}
      {cur.status !== "disabled" && (
        <>
          <textarea className="input min-h-[4rem] text-xs" placeholder="Reply to people at Infinite Fusion" value={(cur.internal ?? "").replace(/<[^>]+>/g, "")} onChange={(e) => set({ internal: e.target.value })} />
          <textarea className="input min-h-[4rem] text-xs" placeholder="Reply to people outside (empty: the same)" value={(cur.external ?? "").replace(/<[^>]+>/g, "")} onChange={(e) => set({ external: e.target.value })} />
          <label className="flex items-center gap-1">Outside people:
            <select className="input !w-auto !py-0.5 text-xs" value={cur.audience ?? "all"} onChange={(e) => set({ audience: e.target.value })}><option value="all">everyone</option><option value="contactsOnly">only my contacts</option><option value="none">nobody</option></select>
          </label>
        </>
      )}
      <button className="btn btn-primary btn-sm" disabled={!d || save.isPending} onClick={() => save.mutate(cur)}>{save.isPending ? "Saving…" : d ? "Save out-of-office" : "Saved"}</button>
      <ErrorNote error={save.error ?? q.error} />
    </div>
  );
}

interface Tpl { id: string; name: string; html: string }
export function Templates() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["mail-templates"], queryFn: () => api<{ items: Tpl[] }>("/api/mail/templates") });
  const [edit, setEdit] = useState<{ id?: string; name: string; html: string } | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ["mail-templates"] });
  const save = useMutation({ mutationFn: (t: { id?: string; name: string; html: string }) => (t.id ? api(`/api/mail/templates/${t.id}`, { method: "PATCH", json: t }) : post("/api/mail/templates", t)), onSuccess: () => { setEdit(null); refresh(); } });
  const remove = useMutation({ mutationFn: (id: string) => del(`/api/mail/templates/${id}`), onSuccess: refresh });
  return (
    <div className="space-y-2 text-xs">
      <div className="flex flex-wrap gap-1.5">
        {(q.data?.items ?? []).map((t) => (
          <span key={t.id} className="chip gap-1"><button onClick={() => setEdit(t)}>{t.name}</button><button aria-label={`Delete ${t.name}`} onClick={() => { if (confirm(`Delete "${t.name}"?`)) remove.mutate(t.id); }}><Trash2 size={11} /></button></span>
        ))}
        <button className="btn btn-sm" onClick={() => setEdit({ name: "", html: "" })}><Plus size={12} /> New quick reply</button>
      </div>
      {edit && (
        <div className="space-y-2">
          <input className="input !py-1 text-sm" placeholder='Name, e.g. "Thanks — will revert by Friday"' value={edit.name} maxLength={80} onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
          <RichEditor key={edit.id ?? "new"} value={edit.html} onChange={(h) => setEdit({ ...edit, html: h })} minHeight={100} placeholder="The text to insert" />
          <div className="flex gap-1.5"><button className="btn btn-primary btn-sm" disabled={!edit.name.trim() || save.isPending} onClick={() => save.mutate(edit)}>Save</button><button className="btn btn-sm" onClick={() => setEdit(null)}>Cancel</button></div>
        </div>
      )}
      <ErrorNote error={save.error ?? remove.error} />
    </div>
  );
}

export function SharedMailboxes({ canPro }: { canPro: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["mail-shared"], queryFn: () => api<{ items: Array<{ address: string; name: string | null }> }>("/api/mail/shared") });
  const [addr, setAddr] = useState("");
  const refresh = () => void qc.invalidateQueries({ queryKey: ["mail-shared"] });
  const add = useMutation({ mutationFn: () => post("/api/mail/shared", { address: addr.trim() }), onSuccess: () => { setAddr(""); refresh(); } });
  const remove = useMutation({ mutationFn: (a: string) => del(`/api/mail/shared/${enc(a)}`), onSuccess: refresh });
  return (
    <div className="space-y-2 text-xs">
      <div className="flex flex-wrap gap-1.5">
        {(q.data?.items ?? []).map((s) => <span key={s.address} className="chip gap-1"><Inbox size={11} /> {s.address}<button aria-label={`Remove ${s.address}`} onClick={() => remove.mutate(s.address)}><X size={11} /></button></span>)}
      </div>
      {canPro ? (
        <form className="flex gap-1.5" onSubmit={(e) => { e.preventDefault(); if (addr.includes("@")) add.mutate(); }}>
          <input className="input !py-1 text-xs" placeholder="info@infinite-fusion.com" value={addr} onChange={(e) => setAddr(e.target.value)} />
          <button className="btn btn-sm" disabled={add.isPending || !addr.includes("@")}>{add.isPending ? "Checking…" : "Add"}</button>
        </form>
      ) : <p className="muted">Needs Mail Pro (above). IT must also give you access to the mailbox in Microsoft 365.</p>}
      <ErrorNote error={add.error ?? remove.error} />
    </div>
  );
}

/** Quick replies in the composer: pick one and it's added to what you're writing. */
export function TemplatePicker({ onPick }: { onPick: (html: string) => void }) {
  const [open, setOpen] = useState(false);
  const q = useQuery({ queryKey: ["mail-templates"], queryFn: () => api<{ items: Tpl[] }>("/api/mail/templates"), enabled: open });
  return (
    <span className="relative">
      <button type="button" className="btn !min-h-0 !px-2 !py-1 text-xs" onClick={() => setOpen((v) => !v)}><Zap size={13} /> Quick replies</button>
      {open && (
        <div className="panel absolute left-0 z-30 mt-1 w-64 p-1 text-xs shadow-xl">
          {(q.data?.items ?? []).map((t) => <button key={t.id} type="button" className="block w-full truncate rounded px-2 py-1.5 text-left hover:bg-black/[0.04]" onClick={() => { onPick(t.html); setOpen(false); }}>{t.name}</button>)}
          {q.data && !q.data.items.length && <p className="muted px-2 py-1.5">No quick replies yet — add them in Mail settings.</p>}
        </div>
      )}
    </span>
  );
}
